import { z } from "zod";
import { PERMISSION_KEYS, ROLE_NAMES, SCOPE_TYPES } from "../permissions";
import { SIGN_IN_STEPS, meMfaSchema } from "./mfa";
import { userPreferencesViewSchema } from "./userPreferences";

/** Auth request and response contracts (FR-10, FR-11, FR-14, SEC-14). */

export const devLoginQuerySchema = z.object({
  as: z.email().max(320),
});
export type DevLoginQuery = z.infer<typeof devLoginQuerySchema>;

/** SEC-14: the reason distinguishes a deliberate sign-out from an idle logoff. */
export const signOutBodySchema = z.object({
  reason: z.enum(["user", "idle"]).default("user"),
});
export type SignOutBody = z.infer<typeof signOutBodySchema>;

export const userStatusSchema = z.enum(["pending", "active", "disabled"]);

/** The user block of `GET /api/v2/me`. */
export const meUserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  displayName: z.string().nullable(),
  status: userStatusSchema,
});
export type MeUser = z.infer<typeof meUserSchema>;

export const meAssignmentSchema = z.object({
  roleName: z.enum(ROLE_NAMES),
  scopeType: z.enum(SCOPE_TYPES),
  tenantId: z.uuid().nullable(),
  instanceId: z.uuid().nullable(),
  /**
   * Group access (2026-09-28): `group` for the default role a directory group carries, `override`
   * for a role an admin raised one member to while they stay in the group. Absent for a direct
   * grant, so the shape stays additive.
   */
  source: z.enum(["group", "override"]).optional(),
  mappingId: z.uuid().nullable().optional(),
  mappingLabel: z.string().nullable().optional(),
});
export type MeAssignment = z.infer<typeof meAssignmentSchema>;

/**
 * One instance the signed-in user can reach, with the role that reaches it and the
 * permissions that role carries. The shell renders its instance picker from this list and
 * hides controls by `permissions`; hiding is not the control (FR-13).
 */
/**
 * #419: the statuses an instance in `GET /me` can have (a disabled one is left out).
 * `setup_failed`: still `provisioning` in `instances.status`, but its newest instance provisioning
 * job failed, so it waits for a PlatformAdmin's Retry (F36), not for time.
 */
export const ME_INSTANCE_STATUSES = ["provisioning", "setup_failed", "active"] as const;

export const meInstanceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  tenantId: z.uuid(),
  /** The tenant's Name (`tenants.name`, platform 0025): fixed, lower case. Not shown as a title. */
  tenantName: z.string(),
  /** The tenant's Display name: what the shell shows before the instance (round 5). */
  tenantDisplayName: z.string(),
  environmentLabel: z.string().nullable(),
  roleName: z.enum(ROLE_NAMES),
  permissions: z.array(z.enum(PERMISSION_KEYS)),
  /** #826: present, `canary`, only on a canary tenant's instance (a PlatformAdmin's picker badge). */
  tenantPurpose: z.literal("canary").optional(),
  /**
   * #419: the instance's own status (`instances.status`). A disabled one never reaches `/me`
   * (`listAccessibleInstances`), so this is `provisioning`, `setup_failed` (its newest setup job
   * failed) or `active`. The admin landing (`landingInstance`) picks only an active one, and the
   * picker badges the other two.
   */
  status: z.enum(ME_INSTANCE_STATUSES),
});
export type MeInstance = z.infer<typeof meInstanceSchema>;

export const meResponseSchema = z.object({
  user: meUserSchema,
  assignments: z.array(meAssignmentSchema),
  instances: z.array(meInstanceSchema),
  /**
   * What the user may do WITHOUT naming an instance: the platform-group permissions their
   * platform-scope role carries, and nothing else (FR-20, FR-13).
   *
   * The platform administration screens act on the deployment, not on one instance, and their
   * routes carry no `x-instance-id`. On a fresh deployment there are no instances at all, so
   * `instances` is empty and this list is the only thing that can tell the shell the picker is
   * not the end of the road — without it the one person who can create an instance cannot reach
   * the screen that creates one.
   *
   * Resolved by the server from the role assignments, like `instances[].permissions`: the client
   * never turns a role name into permissions, so the UI and the route guard can only agree or
   * both be wrong, never disagree.
   */
  platformPermissions: z.array(z.enum(PERMISSION_KEYS)),
  /**
   * Deployment features the shell needs to know about (group access, 2026-09-28: the platform
   * flag `group_mapping`, which only a PlatformAdmin can read in the settings). Optional so a
   * fixture or an older server without it still parses.
   */
  features: z
    .object({
      groupAccess: z.boolean(),
      /** skyMonitor (`features.flags.monitor`): its page, Overview tile and Data health rules. */
      monitor: z.boolean().optional(),
      /** MCP (`features.flags.mcp`, ADR 0009): "Connected apps" and the AI apps admin page. */
      mcp: z.boolean().optional(),
      /**
       * `PUBLIC_APP_URL` is set (B-47): without it `mcp` is false even with the flag on, and the
       * platform settings row says why.
       */
      mcpOriginSet: z.boolean().optional(),
      /** Table-level runs are on in this deployment (`SKYDATA_TABLE_RUNS`). */
      tableRuns: z.boolean().optional(),
      /**
       * #2501: the `features.flags` keys this deployment's policy fixes (`DEPLOYMENT_POLICY`). The
       * booleans above already read the fixed values. Absent: none.
       */
      fixedByDeployment: z.array(z.string()).optional(),
    })
    .optional(),
  /**
   * Admin MFA (2026-09-29): what this sign-in proved, and the deployment's mode. Optional so a
   * fixture or an older server without it still parses.
   */
  mfa: meMfaSchema.optional(),
  /**
   * The person's own display preferences (round 3, 2026-09-30): `timezone` is the IANA zone every
   * time on screen is rendered in, null until one is stored (the client then uses the browser's
   * zone and stores it once). Optional so a fixture or an older server without it still parses.
   */
  preferences: userPreferencesViewSchema.optional(),
  /**
   * Set when every other call would be refused until the person signs in again: `mfa` (the mode
   * forces this person and the sign-in proved no second factor) or `reauth` (an admin's sign-in
   * is older than `auth.admin_session_max_hours`). Absent when nothing is required.
   */
  signInStep: z.enum(SIGN_IN_STEPS).optional(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

export const devLoginResponseSchema = z.object({
  token: z.string(),
  user: meUserSchema,
});
export type DevLoginResponse = z.infer<typeof devLoginResponseSchema>;
